import React from 'react';
import PropTypes from 'prop-types';
import { ClockIcon, TrashIcon } from '@heroicons/react/24/outline';
import MessageHistoryEntry from './MessageHistoryEntry';

/**
 * MessageHistoryPanel - Container component for the message history list
 * Renders a list of MessageHistoryEntry components with header and empty state
 */
const MessageHistoryPanel = ({ historyEntries, onClearHistory, onCopyTopic }) => {
  const hasEntries = historyEntries && historyEntries.length > 0;

  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <ClockIcon className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-800">
            Message History
          </h3>
          {hasEntries && (
            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
              {historyEntries.length} {historyEntries.length === 1 ? 'entry' : 'entries'}
            </span>
          )}
        </div>
        {hasEntries && onClearHistory && (
          <button
            type="button"
            onClick={onClearHistory}
            className="flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
            title="Clear history"
          >
            <TrashIcon className="w-4 h-4 mr-1" />
            Clear History
          </button>
        )}
      </div>

      {/* Content */}
      {hasEntries ? (
        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
          {historyEntries.map((entry) => (
            <MessageHistoryEntry
              key={entry.id}
              timestamp={entry.timestamp}
              topic={entry.topic}
              messageType={entry.messageType}
              topicNumber={entry.topicNumber}
              sentPayload={entry.sentPayload}
              responsePayload={entry.responsePayload}
              status={entry.status}
              errorMessage={entry.errorMessage}
              onCopyTopic={onCopyTopic}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <ClockIcon className="w-12 h-12 mb-3" />
          <p className="text-sm">No messages yet</p>
          <p className="text-xs mt-1">Send a message to see the history here</p>
        </div>
      )}
    </div>
  );
};

MessageHistoryPanel.propTypes = {
  historyEntries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      timestamp: PropTypes.string.isRequired,
      topic: PropTypes.string.isRequired,
      messageType: PropTypes.string.isRequired,
      topicNumber: PropTypes.number.isRequired,
      sentPayload: PropTypes.object,
      responsePayload: PropTypes.object,
      status: PropTypes.oneOf(['success', 'error', 'timeout']).isRequired,
      errorMessage: PropTypes.string,
    })
  ),
  onClearHistory: PropTypes.func,
  onCopyTopic: PropTypes.func,
};

MessageHistoryPanel.defaultProps = {
  historyEntries: [],
  onClearHistory: null,
  onCopyTopic: null,
};

export default MessageHistoryPanel;
